import type { PostHog, Properties } from "posthog-js";
import { optionalImport } from "@/lib/optional-import";
import { isAnalyticsHost, POSTHOG_PROJECT_TOKEN, POSTHOG_PROXY_PATH, POSTHOG_UI_HOST } from "./config";
import type { ClientEvents } from "./events";
import { pageTypeForPath, parsePageContexts, type PageContext } from "./page-context";

// Browser analytics. Components call track(); nothing else imports posthog-js
// directly, so the hostname gate and page context apply everywhere.
//
// PostHog's SDK is 58 KB compressed, so it is not in the page's main
// script: it loads once the page is up (afterPageLoad), and track() keeps what
// happens before that, with its time, to send in order after the page view.
// A failed load turns tracking off for the page and never reloads it
// (optionalImport).

const INTERNAL_FLAG = "hh_internal";

/**
 * An emailed transcript PDF link carries a one-time token in its path
 * (/transcript-pdf/<token>). PostHog would record it with the landing page's
 * events, and then on every later event of the session (the session's entry
 * URL) and in first-touch person properties, so it is redacted everywhere.
 */
const TRANSCRIPT_PDF_LINK_PATH = "/transcript-pdf/";
const TRANSCRIPT_PDF_TOKEN = /(\/transcript-pdf\/)[A-Za-z0-9_-]+/g;

/** `value` with any transcript PDF link token replaced by "[token]", at any depth. */
export function redactTranscriptPdfTokens<T>(value: T): T {
  if (typeof value === "string") {
    return (value.includes(TRANSCRIPT_PDF_LINK_PATH) ? value.replace(TRANSCRIPT_PDF_TOKEN, "$1[token]") : value) as T;
  }
  if (Array.isArray(value)) return value.map((item) => redactTranscriptPdfTokens(item)) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, redactTranscriptPdfTokens(item)]),
    ) as T;
  }
  return value;
}

interface AnalyticsEnvironment {
  location: Pick<URL, "hostname" | "pathname" | "origin">;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  /** Raw JSON from each <script data-hh-analytics> block, in document order. */
  pageContextBlocks: string[];
  /** Calls `start` once PostHog may load; in the browser, afterPageLoad. */
  whenPageIsUp: (start: () => void) => void;
  /** PostHog's SDK; in the browser, a script chunk of its own. */
  loadPostHog: () => Promise<PostHog>;
}

/** The longest PostHog waits for the browser to be idle once the page has loaded. */
const IDLE_TIMEOUT_MS = 2_000;
/** The longest PostHog waits for the load event, which a slow image or embedded player can hold back. */
const LOAD_TIMEOUT_MS = 5_000;
/** Events kept while PostHog loads. Pages send a handful; the cap bounds a page that never loads it. */
const MAX_PENDING_EVENTS = 100;

interface PendingEvent {
  event: string;
  properties: Properties;
  timestamp: Date;
}

/** PostHog once it has started; null before, and for good when tracking is off. */
let client: PostHog | null = null;
/** Events tracked while PostHog loads; null when nothing is loading. */
let pending: PendingEvent[] | null = null;

interface LoadWindow {
  document: Pick<Document, "readyState">;
  addEventListener: (type: "load", listener: () => void, options: { once: true }) => void;
  setTimeout: (callback: () => void, ms: number) => unknown;
  requestIdleCallback?: (callback: () => void, options: { timeout: number }) => unknown;
}

/**
 * Calls `start` once the page is up: at the first idle moment after the load
 * event, so PostHog never competes with the page's own scripts, images and
 * hydration. A load event that has not come within LOAD_TIMEOUT_MS does not
 * hold it back further. A browser without requestIdleCallback starts it
 * straight after the load event.
 */
export function afterPageLoad(start: () => void, win: LoadWindow = window): void {
  let started = false;
  const startOnce = () => {
    if (started) return;
    started = true;
    start();
  };
  const whenIdle = () => {
    if (win.requestIdleCallback) win.requestIdleCallback(startOnce, { timeout: IDLE_TIMEOUT_MS });
    else win.setTimeout(startOnce, 0);
  };
  if (win.document.readyState === "complete") whenIdle();
  else win.addEventListener("load", whenIdle, { once: true });
  win.setTimeout(startOnce, LOAD_TIMEOUT_MS);
}

function browserEnvironment(): AnalyticsEnvironment {
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    // Blocked storage (private mode, strict settings) — tracking still works.
  }
  return {
    location: window.location,
    storage,
    pageContextBlocks: Array.from(
      document.querySelectorAll<HTMLScriptElement>("script[data-hh-analytics]"),
      (el) => el.textContent ?? "",
    ),
    whenPageIsUp: (start) => afterPageLoad(start),
    loadPostHog: () => optionalImport(() => import("posthog-js")).then((module) => module.default),
  };
}

function markInternal(storage: AnalyticsEnvironment["storage"]) {
  try {
    storage?.setItem(INTERNAL_FLAG, "1");
  } catch {
    // Best effort.
  }
}

function isMarkedInternal(storage: AnalyticsEnvironment["storage"]): boolean {
  try {
    return storage?.getItem(INTERNAL_FLAG) === "1";
  } catch {
    return false;
  }
}

/**
 * Starts analytics for this page view. PostHog loads once the page is up; the
 * returned promise settles when it has started, or at once when tracking is
 * off. It never rejects: a PostHog that fails to load turns tracking off.
 */
export function initAnalytics(environment: AnalyticsEnvironment = browserEnvironment()): Promise<void> {
  const { location, storage } = environment;
  if (!isAnalyticsHost(location.hostname)) return Promise.resolve();

  // Our own traffic. A browser is internal once a super admin has been signed
  // in on it (page context) or has loaded an /admin page (the admin routes
  // redirect or 403 everyone else before any script runs). The flag is
  // remembered so browsing signed out on that device stays internal.
  //
  // Internal browsers still send events — tagged is_internal, and filtered in
  // PostHog via Project settings → "Filter out internal and test users"
  // (is_internal ≠ true) — so tracking can be checked in the Activity feed,
  // but their sessions are never recorded.
  if (location.pathname.startsWith("/admin")) {
    markInternal(storage);
    return Promise.resolve();
  }

  const pageContext: PageContext = {
    page_type: pageTypeForPath(location.pathname),
    ...parsePageContexts(environment.pageContextBlocks),
  };
  if (pageContext.is_internal) markInternal(storage);
  const isInternal = isMarkedInternal(storage) || pageContext.is_internal === true;
  // The landing page holds the token in its DOM as well as its URL.
  const isTranscriptPdfLinkPage = location.pathname.startsWith(TRANSCRIPT_PDF_LINK_PATH);

  // The page view keeps this moment's time, when it was captured before PostHog
  // loaded late, so it still comes before the page's own events.
  const viewedAt = new Date();
  pending = [];

  const start = (posthog: PostHog) => {
    posthog.init(POSTHOG_PROJECT_TOKEN, {
      api_host: `${location.origin}${POSTHOG_PROXY_PATH}`,
      ui_host: POSTHOG_UI_HOST,
      // Readers stay anonymous (cheaper); a person profile is created only when
      // a visitor subscribes or signs in.
      person_profiles: "identified_only",
      // Captured manually below, once the page context is in place.
      capture_pageview: false,
      capture_pageleave: true,
      enable_recording_console_log: true,
      disable_session_recording: isInternal || isTranscriptPdfLinkPage,
      // Page context is stamped per event rather than registered as a super
      // property, because registered properties persist and would leak one
      // page's hearing_id onto the next page's events.
      before_send: (event) => {
        if (!event || event.event === "$snapshot") return event;
        event.properties = redactTranscriptPdfTokens({ ...pageContext, ...event.properties });
        if (event.$set) event.$set = redactTranscriptPdfTokens(event.$set);
        if (event.$set_once) event.$set_once = redactTranscriptPdfTokens(event.$set_once);
        return event;
      },
    });

    // Browsers the pre-#1182 code opted out on /admin are ours — opt them back
    // in as internal traffic. Recording is switched off first, because opting
    // in starts it.
    const wasOptedOut = posthog.has_opted_out_capturing();
    if (wasOptedOut) {
      markInternal(storage);
      posthog.set_config({ disable_session_recording: true });
      posthog.opt_in_capturing({ captureEventName: false });
    }
    if (wasOptedOut || isInternal) {
      posthog.register({ is_internal: true });
    }

    posthog.capture("$pageview", undefined, { timestamp: viewedAt });
    for (const { event, properties, timestamp } of pending ?? []) {
      posthog.capture(event, properties, { timestamp });
    }
    pending = null;
    client = posthog;
  };

  return new Promise<void>((resolve) => {
    environment.whenPageIsUp(() => {
      environment
        .loadPostHog()
        .then(start)
        .catch(() => {
          // A script blocked or lost on the way: no tracking on this page.
          pending = null;
        })
        .finally(resolve);
    });
  });
}

export function track<E extends keyof ClientEvents>(event: E, properties: ClientEvents[E]): void {
  if (client) client.capture(event, properties);
  else if (pending && pending.length < MAX_PENDING_EVENTS) pending.push({ event, properties, timestamp: new Date() });
}

/**
 * This visitor's PostHog id, sent alongside form submissions so the server can
 * attribute the outcome to the same person. Undefined when tracking is off, and
 * in the moment before PostHog has loaded.
 */
export function getAnalyticsId(): string | undefined {
  return client?.get_distinct_id();
}

export function resetAnalyticsForTests(): void {
  client = null;
  pending = null;
}
